import { api } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { connectedForBoo, hubAccountsKey } from "@/lib/hub-accounts";
import { useQuery } from "@tanstack/react-query";
import { usePreview } from "../preview-context";
import { connectedKinds } from "./apps";
import { CosChatPreview, CosCompanyPreview, CosSetupPreview, CosWelcomePreview } from "./previews";
import type { ChatProvider, CosProfile } from "./types";

// Right-pane previews for the chief-of-staff branch, fed from the same
// sources the steps use: the session for the name, the preview context for
// the form, onboarding state for the container name and chat platform, and
// the hub accounts for the inbox/calendar state.
export function CosStepPreview({ pathname }: { pathname: string }) {
  const { data: session } = useSession();
  const { orgWebsite, orgName, timeZone, briefingTime } = usePreview();
  const profile: CosProfile = {
    name: session?.user.name ?? "",
    website: orgWebsite,
    companyName: orgName,
    timeZone,
    briefingTime,
  };

  const onboarding = useQuery({ queryKey: ["onboarding-state"], queryFn: api.onboarding.state });
  const booName = onboarding.data?.containerName?.trim() || "Boo";

  if (pathname.startsWith("/onboarding/welcome")) return <CosWelcomePreview name={profile.name} />;
  if (pathname.startsWith("/onboarding/provisioning")) {
    return <CosSetupPreview profile={profile} booName={booName} />;
  }
  if (pathname.startsWith("/onboarding/connect")) {
    const chat: ChatProvider | null = onboarding.data?.slackConnected
      ? "slack"
      : onboarding.data?.teamsConnected
        ? "teams"
        : onboarding.data?.gchatConnected
          ? "gchat"
          : null;
    return (
      <ChatPreview profile={profile} booName={booName} chat={chat} containerId={onboarding.data?.containerId} />
    );
  }
  return <CosCompanyPreview profile={profile} />;
}

function ChatPreview({
  profile,
  booName,
  chat,
  containerId,
}: {
  profile: CosProfile;
  booName: string;
  chat: ChatProvider | null;
  // The Boo being set up; its private inbox/calendar only list in its scope.
  containerId?: string;
}) {
  const accounts = useQuery({
    queryKey: hubAccountsKey(containerId),
    queryFn: () => api.integrations.accounts(containerId),
    enabled: !!containerId,
    retry: false,
  });
  const kinds = connectedKinds(
    containerId ? connectedForBoo(accounts.data?.accounts ?? [], containerId).map((a) => a.app) : [],
  );
  return (
    <CosChatPreview profile={profile} booName={booName} inboxConnected={kinds.size >= 2} chat={chat} />
  );
}
